<template>
  <div v-if="visible" class="dept-popup" :class="{'dept-popup--top': placement === 'top'}">
    <div class="dept-popup-header">选择部门</div>
    <div class="dept-popup-list">
      <div
        v-if="departments.length === 0 && keyword"
        class="dept-popup-empty"
      >
        暂无匹配部门
      </div>
      <div
        v-for="(dept, index) in departments"
        :key="dept.id"
        class="dept-popup-item"
        :class="{ active: index === activeIndex }"
        @mousedown.prevent
        @click="handleSelect(dept)"
        @mouseenter="handleMouseEnter(index)"
      >
        <span class="dept-icon">
          <i class="el-icon-s-home"></i>
        </span>
        <div class="dept-info">
          <span class="dept-name" v-html="highlight(dept.name)"></span>
        </div>
      </div>
    </div>
    <div class="dept-popup-footer">
      <kbd>↑↓</kbd> 导航 <kbd>Enter</kbd> 选择 <kbd>Esc</kbd> 关闭
    </div>
  </div>
</template>

<script>
export default {
  name: 'DepartmentPopup',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    departments: {
      type: Array,
      default: () => []
    },
    activeIndex: {
      type: Number,
      default: 0
    },
    keyword: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      placement: 'bottom'
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.calculatePlacement()
      }
    }
  },
  methods: {
    calculatePlacement() {
      this.$nextTick(() => {
        const popup = this.$el
        if (!popup) return
        const wrapper = popup.parentElement
        if (!wrapper) return
        const wrapperRect = wrapper.getBoundingClientRect()
        const popupHeight = 240
        const spaceTop = wrapperRect.top
        const spaceBottom = window.innerHeight - wrapperRect.bottom

        if (spaceBottom > popupHeight) {
          this.placement = 'bottom'
        } else {
          this.placement = 'top'
        }
      })
    },
    handleSelect(dept) {
      this.$emit('select', dept)
    },
    handleMouseEnter(index) {
      this.$emit('update:activeIndex', index)
    },
    highlight(text) {
      if (!text) return ''
      if (!this.keyword) return this.escapeHtml(text)
      const re = new RegExp(`(${this.escapeRegExp(this.keyword)})`, 'gi')
      return this.escapeHtml(text).replace(re, '<em>$1</em>')
    },
    escapeHtml(str) {
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    },
    escapeRegExp(str) {
      return str.replace(/[.*+?^${}()[\]\\]/g, '\\$&')
    }
  }
}
</script>

<style scoped>
.dept-popup {
  position: absolute;
  z-index: 99999;
  top: calc(100% + 4px);
  left: 0;
  width: 240px;
  height: 240px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  animation: deptFadeInBottom 0.15s ease;
  pointer-events: auto;
}

.dept-popup--top {
  top: auto;
  bottom: calc(100% + 4px);
  animation: deptFadeInTop 0.15s ease;
}

.dept-popup::after {
  content: '';
  position: absolute;
  bottom: -7px;
  left: 16px;
  width: 12px;
  height: 12px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  border-bottom: 1px solid #e4e7ed;
  transform: rotate(45deg);
}

.dept-popup--top::after {
  bottom: auto;
  top: -7px;
  border-right: none;
  border-bottom: none;
  border-left: 1px solid #e4e7ed;
  border-top: 1px solid #e4e7ed;
}

@keyframes deptFadeInTop {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes deptFadeInBottom {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.dept-popup-header {
  padding: 8px 14px;
  font-size: 12px;
  color: #909399;
  border-bottom: 1px solid #f0f0f0;
  background: #fafbfc;
}

.dept-popup-list {
  height: 200px;
  overflow-y: auto;
  padding: 4px 0;
}

.dept-popup-empty {
  text-align: center;
  padding: 20px;
  color: #c0c4cc;
  font-size: 13px;
}

.dept-popup-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px;
  cursor: pointer;
  transition: background 0.12s;
}

.dept-popup-item:hover,
.dept-popup-item.active {
  background: #ecf5ff;
}

.dept-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #67C23A, #85ce61);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.dept-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.dept-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.dept-name ::v-deep em {
  font-style: normal;
  color: #67C23A;
  background: #f0f9eb;
  border-radius: 2px;
  padding: 0 2px;
}

.dept-popup-footer {
  padding: 5px 14px;
  font-size: 11px;
  color: #c0c4cc;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 3px;
}

.dept-popup-footer kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  background: #f5f7fa;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  font-size: 10px;
  color: #606266;
  padding: 0 4px;
  font-family: inherit;
}
</style>